import React from 'react';
import ReactMarkdown from 'react-markdown';
import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { LayoutComposer, IconSpinner, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { camelize } from '../../util/string';
import { propTypes } from '../../util/types';

import { ASSET_NAME } from './AboutUsPage.duck';
import sampleData from './fallback-about-us.json';

import css from './AboutUsPage.module.css';
import SectionTextMaybe from '../ListingPage/SectionTextMaybe';

const AUTHOR_SIGNATURE = '/static/images/ceo-signature.webp';
const AUTHOR_PHOTO = '/static/images/ceo-photo.webp';

// Parse markdown content from page asset data
const parseMarkdownContent = (data) => {
  try {
    if (!data?.sections || data.sections.length === 0) {
      return null;
    }

    const firstSection = data.sections[0];
    const firstBlock = firstSection.blocks?.[0];

    if (firstBlock?.text?.content) {
      return firstBlock.text.content;
    }

    return null;
  } catch (error) {
    console.error('Error parsing markdown content:', error);
    return null;
  }
};

const getPageHeading = (data) => {
  const mainSection = data.sections[0];
  return {
    title: mainSection.title?.content || 'About Us',
    description: mainSection.description?.content || 'Learn more about our story',
  };
};

// Extract metadata from page asset data
const getMetadata = (data) => {
  try {
    const meta = data?.meta || {};
    return {
      title: meta.pageTitle?.content || 'About Us',
      description: meta.pageDescription?.content || 'Learn more about our story',
    };
  } catch (error) {
    console.error('Error extracting metadata:', error);
    return {
      title: 'About Us',
      description: 'Learn more about our story',
    };
  }
};

// Custom footer with signature
const CustomFooter = ({ authorName = 'Anubhav Srivastava', authorTitle = 'Co-Founder & CEO' }) => {
  return (
    <div className={css.customFooter}>
      <div className={css.signature}>
        <img
          src={AUTHOR_SIGNATURE}
          alt={`${authorName} signature`}
          className={css.signatureImage}
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
        <div className={css.signatureDetails}>
          <img
            src={AUTHOR_PHOTO}
            alt={authorName}
            className={css.authorPhoto}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <div className={css.authorInfo}>
            <h3 className={css.authorName}>{authorName}</h3>
            <p className={css.authorTitle}>{authorTitle}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main AboutUs page component
const AboutUsPageComponent = props => {
  const { pageAssetsData, inProgress, error } = props;

  const layoutAreas = `
    topbar
    main
    footer
  `;

  // Get the about-us data from the pageAssetsData (same pattern as PrivacyPolicyPage)
  // This should be: { sections: [...], meta: {...} }
  const aboutUsData = pageAssetsData?.[camelize(ASSET_NAME)]?.data;

  // Use sample data as fallback if no CMS data is available
  const dataToUse = aboutUsData || sampleData.data;

  // Parse content
  const markdownContent = parseMarkdownContent(dataToUse);
  const pageHeading = getPageHeading(dataToUse);
  const metadata = getMetadata(dataToUse);

  // Debug logging
  console.log('AboutUsPage - aboutUsData:', aboutUsData);
  console.log('AboutUsPage - dataToUse:', dataToUse);
  console.log('AboutUsPage - markdownContent:', markdownContent ? 'Found' : 'Not found');

  // Loading state
  if (inProgress && !markdownContent) {
    return (
      <Page title={metadata.title} scrollingDisabled={false}>
        <LayoutComposer areas={layoutAreas} className={css.root}>
          {props => {
            const { Topbar, Main, Footer } = props;
            return (
              <>
                <Topbar as="header">
                  <TopbarContainer currentPage="AboutUsPage" />
                </Topbar>
                <Main as="main">
                  <div className={css.loading}>
                    <IconSpinner delay={600} />
                  </div>
                </Main>
                <Footer>
                  <FooterContainer />
                </Footer>
              </>
            );
          }}
        </LayoutComposer>
      </Page>
    );
  }

  // Main content render
  return (
    <Page
      title={metadata.title}
      description={metadata.description}
      scrollingDisabled={false}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'AboutPage',
        name: metadata.title,
        description: metadata.description,
      }}
    >
      <LayoutComposer areas={layoutAreas} className={css.root}>
        {props => {
          const { Topbar, Main, Footer } = props;
          return (
            <>
              <Topbar as="header">
                <TopbarContainer currentPage="AboutUsPage" />
              </Topbar>
              <Main as="main">
                {/* Hero section */}
                <div className={css.heroSection}>
                  <h1 className={css.heroHeading}>{pageHeading.title}</h1>
                  <div className={css.heroSubheading}>
                    <SectionTextMaybe text={pageHeading.description} />
                  </div>
                </div>

                <div className={css.stackContainer}>
                  <div className={css.contentWrapper}>
                    <article className={css.contentCard}>
                      {markdownContent ? (
                        <div className={css.markdownContent}>
                          <SectionTextMaybe text={markdownContent} />
                        </div>
                      ) : (
                        <div>
                          <h1 className={css.heading}>About Us</h1>
                          <p className={css.paragraph}>
                            {error ? 'Unable to load content.' : 'Content is being loaded...'}
                          </p>
                        </div>
                      )}
                      <CustomFooter />
                    </article>
                  </div>
                </div>
              </Main>
              <Footer>
                <FooterContainer />
              </Footer>
            </>
          );
        }}
      </LayoutComposer>
    </Page>
  );
};

AboutUsPageComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
  error: propTypes.error,
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  return { pageAssetsData, inProgress, error };
};

const AboutUsPage = compose(connect(mapStateToProps))(AboutUsPageComponent);

const ABOUT_US_ASSET_NAME = ASSET_NAME;
export { ABOUT_US_ASSET_NAME, AboutUsPageComponent };

export default AboutUsPage;
